import React, { Component } from "react";
import {
  Layout,
  Breadcrumb,
  PageHeader,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Statistic,
  Card,
  Typography,
  Empty,
  Spin
} from "antd";
import PieChartPlannedIntervention from "./PieChartPlannedIntervention";
import {
  RightOutlined,
  CalendarOutlined,
  MedicineBoxOutlined,
  StopOutlined,
  PieChartOutlined
} from "@ant-design/icons";

import { all as AllStats } from "../../api/SurgicalInterventionsStats";
import { all as AllCareUnit } from "../../api/CareUnit.js";

import { cancellationReasons, reScheduleReasons } from "../../constants";
import moment from "moment";
import locale from 'antd/lib/locale/fr_FR';

const { Content } = Layout;
const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const layoutSelect = {
  showSearch: true,
  allowClear: true,
  optionFilterProp: "children",
  filterOption: false,
  defaultActiveFirstOption: false,
  style: {
    width: '100%',
    marginTop: 8,
  }
};

const emptyLayout = {
  image: <Text type="secondary"><PieChartOutlined /></Text>,
  style: {
    margin: "100px 0",
  },
};

/**
 *
 */
class SurgicalInterventionsStats extends Component {
  /**
   *
   * @param {*} props
   */
  constructor(props) {
    super(props);

    this.title = "Statistiques des séjours";

    this.state = {
      loading: false,
      surgicalInterventionsStats: null,
      firstDay: moment().subtract(1, "weeks"),
      lastDay: moment(),
      careUnits: [],
      selectedCareUnitIds: []
    };

    this.onChangeDate = this.onChangeDate.bind(this);
    this.onChangeCareUnits = this.onChangeCareUnits.bind(this);
  }

  componentDidMount() {
    this.fetch();
    this.fetchCareUnits();
  }

  getPayload() {
    let { selectedCareUnitIds } = this.state;

    let payload = {};
    if (selectedCareUnitIds.length) {
      payload['care_unit_ids'] = selectedCareUnitIds;
    }

    return payload;
  }

  fetch() {
    this.setState({ loading: true }, () => {
      let payload = this.getPayload();
      let { firstDay, lastDay } = this.state;

      AllStats(payload, firstDay, lastDay)
        .then((data) => {
          this.setState({
            surgicalInterventionsStats: data.results,
            loading: false
          });
        });
    });
  }

  /**
   * 
   * @returns 
   */
  fetchCareUnits() {
    return AllCareUnit({
      current: 1,
      pageSize: 100,
      q: {},
    })
      .then((data) => {
        this.setState({ careUnits: data.results });
      });
  }

  /**
   * 
   * @param {*} dates 
   * @param {*} dateStrings 
   */
  onChangeDate(dates, dateStrings) {
    if (dates === null) {
      let firstDay = moment().subtract(1, "weeks");
      let lastDay = moment();
      dates = [firstDay, lastDay];
    }

    this.setState({
      firstDay: dates[0],
      lastDay: dates[1],
    }, () => {
      this.fetch();
    });
  }

  /**
   *
   * @param {*} value
   */
  onChangeCareUnits(value) {
    this.setState({ selectedCareUnitIds: value }, () => {
      this.fetch();
    });
  }

  render() {
    let {
      surgicalInterventionsStats,
      careUnits,
      firstDay,
      lastDay,
      loading
    } = this.state;

    let careUnitOptions = careUnits.map((option, index) => (
      <Option key={index} value={option.id}>{option.name}</Option>
    ));

    const dataCancelled = cancellationReasons.map(reason => {
      let value = (!!surgicalInterventionsStats)
        ? surgicalInterventionsStats['stats']['cancelled_reason'][reason.id] : 0;

      return { name: reason.label, value };
    });

    const dataReschedule = reScheduleReasons.map(reason => {
      let value = (!!surgicalInterventionsStats)
        ? surgicalInterventionsStats['stats']['reschedule_reason'][reason.id] : 0;

      return { name: reason.label, value };
    });

    let { current=0, cancelled=0, reschedule=0 } = (!!surgicalInterventionsStats) ? surgicalInterventionsStats['stats'] : {};

    let percentCanceled = (current > 0) ? (cancelled/current * 100).toFixed(0) : 0;
    let percentReschedule = (current > 0) ? (reschedule/current * 100).toFixed(0) : 0;

    return (
      <Content
        style={{
          margin: "24px 16px",
          padding: 24,
          minHeight: 280,
        }}
      >
        <Breadcrumb
          style={{ padding: 24 }}
          separator={<RightOutlined style={{ fontSize: "75%" }} />}
        >
          <Breadcrumb.Item>
            <a href="/home">Accueil</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Reporting</Breadcrumb.Item>
          <Breadcrumb.Item>{this.title}</Breadcrumb.Item>
        </Breadcrumb>
        <PageHeader ghost={false} title={this.title} subTitle="sur une période" />
        <div
          className="site-layout-background"
          style={{ padding: 24 }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space className="mb-4" direction="horizontal" align="end">
              <Space direction="vertical">
                <label>Dates :</label>
                <RangePicker
                  format="DD/MM/YYYY"
                  locale={locale}
                  ranges={{
                    "Aujourd'hui": [moment(), moment()],
                    "Cette semaine": [
                      moment().startOf("week"),
                      moment().endOf("week"),
                    ],
                    "Ce mois": [
                      moment().startOf("month"),
                      moment().endOf("month"),
                    ],
                    "Les 7 derniers jours": [moment().subtract(7, 'days'), moment()],
                    "Les 7 prochains jours": [moment(), moment().add(7, 'days')],
                    "Les 30 derniers jours": [moment().subtract(30, 'days'), moment()]
                  }}
                  defaultValue={[firstDay, lastDay]}
                  onChange={this.onChangeDate}
                  allowClear={false}
                />
              </Space>
            </Space>
            <Row className="mb-4" style={{ width: "100%" }}>
              <Col span={8} className="pr-3">
                <label>Unité(s) de soin(s) :</label>
                <Select
                  {...layoutSelect}
                  mode="multiple"
                  placeholder="Choisissez votre/vos unité(s)"
                  onChange={this.onChangeCareUnits}
                >
                  {careUnitOptions}
                </Select>
              </Col>
            </Row>
          </Space>
          <Row className="mt-2" gutter={16}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Nombre total de séjours"
                  value={current}
                  prefix={<MedicineBoxOutlined />}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Nombre de séjours annulés"
                  value={cancelled}
                  prefix={<StopOutlined />}
                  suffix={(current > 0) ? <Text>{percentCanceled}%</Text> : null}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Nombre de séjours reprogrammés"
                  value={reschedule}
                  prefix={<CalendarOutlined />}
                  suffix={(current > 0) ? <Text>{percentReschedule}%</Text> : null}
                  loading={loading}
                />
              </Card>
            </Col>
          </Row>
          <br />
          <Spin spinning={loading}>
            <Row className="mb-4" gutter={16}>
              <Col span={12}>
                <PageHeader
                  ghost={false}
                  title={"Répartition des annulations de séjours"}
                />
                {(cancelled > 0) ?
                  <PieChartPlannedIntervention data={dataCancelled} />
                :
                  <Empty {...emptyLayout} description="Aucun séjour annulé" />
                }
              </Col>
              <Col span={12}>
                <PageHeader
                  ghost={false}
                  title={"Répartition des modifications de séjours"}
                />
                {(reschedule > 0) ?
                  <PieChartPlannedIntervention data={dataReschedule} />
                :
                  <Empty {...emptyLayout} description="Aucun séjour reprogrammé" />
                }
              </Col>
            </Row>
          </Spin>
        </div>
      </Content>
    );
  }
}

export default SurgicalInterventionsStats;
